import { createElement, useCallback, useMemo, useState } from 'react';
import type { HoaDonMuaHangTrongNuoc, HoaDonMuaHangTrongNuocInput } from '@/types/schemas';
import { HOA_DON_MUA_HANG_TRONG_NUOC_TABLE_TABS, TABLE_PAGE_SIZE } from '../constants';
import { initialSearchValues, SearchFormValues } from '../schema';
import { useSearchFieldStates, useSearchTable } from '../hooks';
import { DATA_TABLE_COLUMNS } from '../cols-definition';
import { QUERY_KEYS } from '@/constants/query-keys';
import { useCRUD, useForms } from '@/hooks';

export const useHoaDonMuaHangTrongNuocTable = () => {
  const [showSearchDialog, setShowSearchDialog] = useState(false);
  const [searchParams, setSearchParams] = useState<SearchFormValues>(initialSearchValues);

  const {
    data,
    detail,
    fetchDetail,
    addItem,
    deleteItem,
    refreshData,
    isLoading,
    handlePageChange,
    totalItems,
    currentPage,
    updateItem,
    sumTotal,
    searchData,
    isFiltered,
    isSearching
  } = useCRUD<HoaDonMuaHangTrongNuoc, HoaDonMuaHangTrongNuocInput>({
    endpoint: QUERY_KEYS.HOA_DON_MUA_HANG_TRONG_NUOC,
    pageSize: TABLE_PAGE_SIZE,
    sumField: 't_tt_nt',
    useFilter: true
  });

  const {
    showForm,
    showDelete,
    formMode,
    isCopyMode,
    selectedObj,
    selectedRowIndex,
    showBulkDelete,
    handleRowClick,
    clearSelection,
    handleCloseBulkDelete,
    handleCloseForm,
    handleCloseDelete,
    handleAddClick,
    handleEditClick,
    handleViewClick,
    handleDeleteClick,
    handleCopyClick
  } = useForms(fetchDetail);

  const { state, actions } = useSearchFieldStates();
  const {
    handleSearchChange,
    onPageChange,
    handleInitialSearch,
    handleRefreshClick,
    isFiltering,
    searchState,
    hasFilterParams
  } = useSearchTable({
    searchData,
    searchParams,
    setSearchParams,
    isFiltered,
    handlePageChange
  });

  const handleSearchOpen = useCallback(() => {
    setShowSearchDialog(true);
  }, []);

  const handleSearchClose = useCallback(() => {
    setShowSearchDialog(false);
  }, []);

  const handleSearchSubmit = useCallback(
    async (filters: SearchFormValues) => {
      await handleInitialSearch(filters);
      setShowSearchDialog(false);
    },
    [handleInitialSearch]
  );

  const handleRefresh = useCallback(async () => {
    await handleRefreshClick();
    await fetchDetail(selectedObj?.uuid);
  }, [fetchDetail, handleRefreshClick, selectedObj?.uuid]);

  const displayData = hasFilterParams ? searchState.searchResults : data;
  const displayIsLoading = isLoading || isFiltering;
  const resolvedSumTotal = hasFilterParams ? (searchState.totalRow?.t_tt_nt ?? sumTotal) : sumTotal;
  const columns = useMemo(
    () => DATA_TABLE_COLUMNS(handleViewClick, resolvedSumTotal, displayIsLoading),
    [displayIsLoading, handleViewClick, resolvedSumTotal]
  );
  const tables = useMemo(
    () =>
      HOA_DON_MUA_HANG_TRONG_NUOC_TABLE_TABS.map(tab => ({
        name: tab.name,
        rows: tab.status ? displayData.filter(row => String(row?.status ?? '') === tab.status) : displayData,
        columns,
        icon: tab.color
          ? createElement('div', {
              className: 'mr-2 size-[7px] rounded-full',
              style: { backgroundColor: tab.color }
            })
          : undefined
      })),
    [columns, displayData]
  );

  return {
    crud: {
      detail,
      fetchDetail,
      addItem,
      deleteItem,
      refreshData,
      updateItem,
      searchData
    },
    selection: {
      selectedObj,
      selectedRowIndex,
      clearSelection
    },
    dialogs: {
      showForm,
      showDelete,
      showBulkDelete,
      isCopyMode
    },
    form: {
      formMode,
      handleRowClick,
      handleCloseBulkDelete,
      handleCloseForm,
      handleCloseDelete,
      handleAddClick,
      handleEditClick,
      handleViewClick,
      handleDeleteClick,
      handleCopyClick
    },
    searchDialog: {
      open: showSearchDialog,
      searchParams,
      searchFieldState: state,
      searchFieldActions: actions,
      onOpen: handleSearchOpen,
      onClose: handleSearchClose,
      onSubmit: handleSearchSubmit
    },
    table: {
      tables,
      columns,
      displayData,
      displayIsLoading,
      currentPage: hasFilterParams ? searchState.currentPage : currentPage,
      totalItems: hasFilterParams ? searchState.totalCount : totalItems,
      onPageChange,
      onSearchChange: handleSearchChange,
      onRefresh: handleRefresh
    }
  };
};
